// ui/src/components/shared/PlanNarrativeSummary.jsx

/**
 * @file PlanNarrativeSummary.jsx
 * @description Renders a server-generated deterministic plan narrative.
 *   Does not inspect or infer from raw plan configuration — all prose is
 *   sourced from the server payload. Supports three personas: ca2, manager,
 *   rep. Exports PlanNarrativeSummarySkeleton for loading states.
 *   Consumed by C-62 PlanNarrativeSummary, C-26 PlanApprovalReview,
 *   C-38 RepPlanSummary.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileB_v4_final.md §2.7 — GET /api/v1/plans/{id}/narrative (ep 35)
 * @see api_spec_fileB_v4_final.md §6.2 — GET /api/v1/rep/{id}/plan-summary (ep 49)
 */

import { useId, useState } from 'react';
import {
  fullDateTime,
  relativeTime,
} from './ProvisionalBadge';
import DataFreshnessIndicator from './DataFreshnessIndicator';

/**
 * @typedef {{
 *   text: string,
 *   confidence: 'high' | 'partial' | 'unavailable',
 *   constructKey: string
 * }} NarrativeTier1Fragment
 */

/**
 * @typedef {{
 *   constructKey: string,
 *   constructLabel: string,
 *   summary: string,
 *   confidence: 'high' | 'partial' | 'unavailable',
 *   detail?: string
 * }} NarrativeTier2Construct
 */

/**
 * @typedef {{
 *   constructKey: string,
 *   constructLabel: string,
 *   reason: string
 * }} NarrativeGap
 */

/**
 * @typedef {{
 *   tier1: NarrativeTier1Fragment[],
 *   tier2: NarrativeTier2Construct[],
 *   gaps: NarrativeGap[],
 *   coverageCount: number,
 *   totalConstructCount: number,
 *   generatedAt: string,
 *   governanceRationale?: string
 * }} PlanSummaryPayload
 */

/**
 * Renders a server-generated deterministic plan narrative.
 * It does not inspect or infer from raw plan configuration.
 *
 * @param {{
 *   planSummaryPayload: PlanSummaryPayload | null,
 *   persona: 'ca2' | 'rep' | 'manager',
 *   showGovernanceRationale?: boolean,
 *   className?: string
 * }} props
 */
export default function PlanNarrativeSummary({
  planSummaryPayload,
  persona,
  showGovernanceRationale = false,
  className = '',
}) {
  if (!planSummaryPayload) {
    return <UnavailableSummary className={className} />;
  }

  const tier1 = Array.isArray(planSummaryPayload.tier1)
    ? planSummaryPayload.tier1
    : [];

  const tier2 = Array.isArray(planSummaryPayload.tier2)
    ? planSummaryPayload.tier2
    : [];

  const gaps = Array.isArray(planSummaryPayload.gaps)
    ? planSummaryPayload.gaps
    : [];

  const visibleTier1 = tier1.filter(
    (fragment) => fragment.confidence !== 'unavailable',
  );

  /*
   * Server-supplied summary text is still the sole source of economic prose.
   * Persona filtering changes depth only.
   *
   * The supplied payload contains no server-authored visibility/layer property.
   * Therefore we do not infer "Settlement" or another economic category from
   * constructKey. Rep receives only constructs whose supplied confidence is
   * renderable; governance rationale is always excluded.
   */
  const visibleTier2 = tier2.filter(
    (construct) => construct.confidence !== 'unavailable',
  );

  if (visibleTier1.length === 0 && visibleTier2.length === 0) {
    return (
      <div className={className}>
        <UnavailableSummary />

        {gaps.length > 0 && (
          <NarrativeGaps gaps={gaps} persona={persona} />
        )}
      </div>
    );
  }

  const freshness = getFreshnessState(planSummaryPayload.generatedAt);

  return (
    <section
      aria-label="Platform-generated plan summary"
      className={[
        'rounded-[12px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-surface))]',
        className,
      ].join(' ')}
    >
      <div className="p-4">
        <header>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
                Plan summary
              </h2>

              <p
                title={
                  fullDateTime(planSummaryPayload.generatedAt) ||
                  planSummaryPayload.generatedAt
                }
                className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]"
              >
                Summary generated{' '}
                {summaryRelativeTime(planSummaryPayload.generatedAt)}
              </p>
            </div>

            {persona !== 'rep' && (
              <span
                aria-label={`Summary covers ${planSummaryPayload.coverageCount} of ${planSummaryPayload.totalConstructCount} plan constructs`}
                className={[
                  'rounded-[999px]',
                  'bg-[rgb(var(--color-surface-muted))]',
                  'px-2 py-1 text-[10px] font-medium',
                  'text-[rgb(var(--color-text-muted))]',
                ].join(' ')}
              >
                Covering {planSummaryPayload.coverageCount} of{' '}
                {planSummaryPayload.totalConstructCount} plan constructs
              </span>
            )}
          </div>

          {freshness.showWarning && (
            <div className="mt-2">
              <DataFreshnessIndicator
                signal="projection"
                freshnessAt={planSummaryPayload.generatedAt}
                status="warning"
              />
            </div>
          )}
        </header>

        <Tier1Narrative fragments={visibleTier1} />

        <Tier2Narrative
          constructs={visibleTier2}
          persona={persona}
        />

        {gaps.length > 0 && (
          <NarrativeGaps gaps={gaps} persona={persona} />
        )}

        {persona === 'ca2' &&
          showGovernanceRationale &&
          planSummaryPayload.governanceRationale && (
            <GovernanceRationale
              text={planSummaryPayload.governanceRationale}
            />
          )}
      </div>
    </section>
  );
}

function Tier1Narrative({ fragments }) {
  if (!fragments.length) return null;

  return (
    <div className="mt-4">
      <p className="text-[13px] leading-6 text-[rgb(var(--color-text))]">
        {fragments.map((fragment, index) => (
          <span key={`${fragment.constructKey}-${index}`}>
            {index > 0 ? ' ' : ''}
            {fragment.text}
            {fragment.confidence === 'partial' && (
              <>
                {' '}
                <span
                  title="This server-generated sentence has partial rendering confidence"
                  aria-label="Partial rendering confidence"
                  className="font-semibold text-[rgb(var(--color-warning))]"
                >
                  ·
                </span>
              </>
            )}
          </span>
        ))}
      </p>
    </div>
  );
}

function Tier2Narrative({ constructs, persona }) {
  if (!constructs.length) return null;

  if (persona === 'rep') {
    return (
      <div className="mt-4">
        <h3 className="text-[12px] font-semibold text-[rgb(var(--color-text))]">
          How your plan works
        </h3>

        <ul className="mt-2 space-y-2">
          {constructs.map((construct) => (
            <li
              key={construct.constructKey}
              aria-label={`${construct.constructLabel}. ${construct.summary}`}
              className="text-[12px] leading-5 text-[rgb(var(--color-text))]"
            >
              {construct.summary}

              {construct.confidence === 'partial' && (
                <span className="ml-1 text-[10px] font-medium text-[rgb(var(--color-warning))]">
                  · partial summary
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  if (persona === 'manager') {
    return (
      <div className="mt-4 space-y-2">
        <h3 className="text-[12px] font-semibold text-[rgb(var(--color-text))]">
          Plan terms
        </h3>

        {constructs.map((construct) => (
          <div
            key={construct.constructKey}
            aria-label={`${construct.constructLabel}. ${construct.summary}`}
            className="flex gap-2 text-[12px] leading-5"
          >
            <span className="shrink-0 font-semibold text-[rgb(var(--color-text))]">
              {construct.constructLabel}:
            </span>

            <span className="text-[rgb(var(--color-text-muted))]">
              {construct.summary}
              {construct.confidence === 'partial'
                ? ' · Partial summary'
                : ''}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="mt-4">
      <div className="mb-2">
        <h3 className="text-[12px] font-semibold text-[rgb(var(--color-text))]">
          Rendered plan constructs
        </h3>

        <p className="mt-1 text-[11px] text-[rgb(var(--color-text-muted))]">
          Construct grouping is not inferred in the browser. The summaries
          below are rendered from the deterministic server payload.
        </p>
      </div>

      <div className="space-y-2">
        {constructs.map((construct) => (
          <Ca2Construct
            key={construct.constructKey}
            construct={construct}
          />
        ))}
      </div>
    </div>
  );
}

function Ca2Construct({ construct }) {
  const [expanded, setExpanded] = useState(false);
  const detailId = useId();
  const hasDetail = Boolean(construct.detail);

  return (
    <article
      aria-label={`${construct.constructLabel}. ${construct.summary}`}
      className={[
        'rounded-[8px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-bg))]',
        'p-3',
      ].join(' ')}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="text-[12px] font-semibold text-[rgb(var(--color-text))]">
              {construct.constructLabel}
            </h4>

            {construct.confidence === 'partial' && (
              <span
                className={[
                  'rounded-[999px]',
                  'bg-[rgb(var(--color-warning)/0.1)]',
                  'px-2 py-0.5 text-[10px] font-semibold',
                  'text-[rgb(var(--color-warning))]',
                ].join(' ')}
              >
                Partial rendering
              </span>
            )}
          </div>

          <p className="mt-1 text-[12px] leading-5 text-[rgb(var(--color-text))]">
            {construct.summary}
          </p>
        </div>

        {hasDetail && (
          <button
            type="button"
            className="text-button shrink-0"
            aria-expanded={expanded}
            aria-controls={detailId}
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? 'Hide detail' : 'Show detail'}
          </button>
        )}
      </div>

      {hasDetail && (
        <div id={detailId} hidden={!expanded}>
          <p className="mt-3 border-t border-[rgb(var(--color-border))] pt-3 text-[12px] leading-5 text-[rgb(var(--color-text-muted))]">
            {construct.detail}
          </p>
        </div>
      )}
    </article>
  );
}

function NarrativeGaps({ gaps, persona }) {
  const labels = gaps.map((gap) => gap.constructLabel).join(', ');

  const headline =
    persona === 'ca2'
      ? `The following constructs require manual review before approval: ${labels}`
      : persona === 'rep'
        ? `Some plan terms require detailed review. Contact your administrator: ${labels}`
        : `Some plan terms require review: ${labels}`;

  return (
    <aside
      role="note"
      aria-label="Plan terms requiring review"
      className="modifier-manager-warning mt-4"
    >
      <p className="text-[12px] font-medium">{headline}</p>

      {persona === 'ca2' && (
        <ul className="mt-2 space-y-1">
          {gaps.map((gap) => (
            <li key={gap.constructKey} className="text-[11px]">
              <strong>{gap.constructLabel}:</strong> {gap.reason}
            </li>
          ))}
        </ul>
      )}
    </aside>
  );
}

function GovernanceRationale({ text }) {
  return (
    <aside
      aria-label="Administrator rationale — separate from plan terms"
      className="mt-5 border-t border-[rgb(var(--color-border))] pt-4"
    >
      <p className="text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
        Administrator rationale
      </p>

      <p className="mt-2 text-[12px] leading-5 text-[rgb(var(--color-text))]">
        "{text}"
      </p>

      <p className="mt-1 text-[10px] text-[rgb(var(--color-text-muted))]">
        Governance context supplied by the administrator. This is separate from
        the platform-generated economic plan summary.
      </p>
    </aside>
  );
}

function UnavailableSummary({ className = '' }) {
  return (
    <div
      role="status"
      className={[
        'rounded-[12px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-surface))]',
        'p-4 text-[12px]',
        'text-[rgb(var(--color-text-muted))]',
        className,
      ].join(' ')}
    >
      Plan summary is not yet available.
    </div>
  );
}

export function PlanNarrativeSummarySkeleton({ className = '' }) {
  return (
    <div
      aria-busy="true"
      aria-label="Loading plan summary"
      className={[
        'rounded-[12px]',
        'border border-[rgb(var(--color-border))]',
        'bg-[rgb(var(--color-surface))]',
        'p-4',
        className,
      ].join(' ')}
    >
      <div className="skeleton h-4 w-full rounded" />
      <div className="skeleton mt-2 h-4 w-5/6 rounded" />
      <div className="skeleton mt-2 h-4 w-2/3 rounded" />
    </div>
  );
}

function summaryRelativeTime(isoString) {
  if (!isoString) return 'at an unknown time';

  const result = relativeTime(isoString);

  if (!result) return 'at an unknown time';

  if (result === 'Just updated') return 'just now';

  if (result.startsWith('Updated ')) {
    return result.slice('Updated '.length);
  }

  return result;
}

function getFreshnessState(generatedAt) {
  if (!generatedAt) {
    return {
      showWarning: true,
    };
  }

  const generatedTime = new Date(generatedAt).getTime();

  if (Number.isNaN(generatedTime)) {
    return {
      showWarning: true,
    };
  }

  return {
    showWarning: Date.now() - generatedTime > 24 * 60 * 60 * 1000,
  };
}
